import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const DropdownItem = ({ item, isActive, onMouseEnter, onMouseLeave }) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  
  if (item.submenu) {
    return (
      <div 
        className="dropdown-item has-submenu"
        onMouseEnter={() => {
          setIsSubmenuOpen(true);
          onMouseEnter?.();
        }}
        onMouseLeave={() => {
          setIsSubmenuOpen(false);
          onMouseLeave?.();
        }}
      >
        <span className="submenu-label">
          {item.label}
        </span>
        <div className={`submenu ${isSubmenuOpen ? 'show' : ''}`}>
          {item.submenu.map((subItem, subIndex) => (
            <Link
              key={subIndex}
              to={subItem.path}
              className="submenu-item"
            >
              <div className="submenu-item-content">
                <div className="submenu-item-header">
                  <span className="submenu-item-title">{subItem.label}</span>
                  {subItem.price && (
                    <span className="submenu-item-price">{subItem.price}</span>
                  )}
                </div>
                {subItem.description && (
                  <p className="submenu-item-description">{subItem.description}</p>
                )}
                {subItem.duration && (
                  <div className="submenu-item-meta">
                    <span className="duration">{subItem.duration}</span>
                    {subItem.rating && (
                      <span className="rating">
                        {subItem.rating} ★
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Link 
      to={item.path}
      className={`dropdown-item ${isActive ? 'active' : ''}`}
      role="menuitem"
      tabIndex="-1"
    >
      {item.label}
      {isActive && (
        <span className="active-indicator" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      )}
    </Link>
  );
};

const DropdownMenu = ({ title, items, isOpen, onClick, onMouseEnter, onMouseLeave }) => {
  const location = useLocation();
  const dropdownRef = useRef(null);
  const hasActiveItem = (items) => {
    return items.some(item => {
      if (item.submenu) {
        return item.submenu.some(subItem => location.pathname === subItem.path);
      }
      return location.pathname === item.path;
    });
  };
  const isActive = hasActiveItem(items);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen) {
        onClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClick]);

  // Close dropdown when route changes
  useEffect(() => {
    if (isOpen) {
      onClick();
    }
  }, [location.pathname, isOpen, onClick]);

  return (
    <div 
      className={`dropdown ${isActive ? 'active' : ''}`} 
      ref={dropdownRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button 
        className={`dropdown-toggle ${isOpen ? 'active' : ''}`}
        onClick={onClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {title}
        <span className="dropdown-arrow" aria-hidden="true">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      
      <div 
        className={`dropdown-menu ${isOpen ? 'show' : ''}`} 
        role="menu"
        aria-labelledby="dropdownMenuButton"
      >
        {items.map((item, index) => {
          const isItemActive = item.submenu 
            ? item.submenu.some(subItem => location.pathname === subItem.path)
            : location.pathname === item.path;
            
          return (
            <DropdownItem 
              key={index}
              item={item}
              isActive={isItemActive}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(DropdownMenu);
